import { useEffect,useRef, useState } from 'react';

import { Button, Stack, Text, toast } from '@ekhodealer/ekho-common/rev';

import type { ActionItem } from '@ekhodealer/ekho-common';

interface SignDocumentDetailProps {
	actionItem: ActionItem;
}

/**
 * SignDocumentDetail component for signing documents via Pandadoc.
 * Attempts to embed Pandadoc in an iframe, with fallback to opening in a new tab.
 */
export const SignDocumentDetail = ({ actionItem }: SignDocumentDetailProps) => {
	const signatureUrl = actionItem.metadata?.sellerSignatureUrl as string | undefined;
	const [iframeError, setIframeError] = useState(false);
	const iframeRef = useRef<HTMLIFrameElement>(null);

	// Detect if iframe fails to load (X-Frame-Options blocking)
	useEffect(() => {
		if (iframeRef.current && signatureUrl) {
			const iframe = iframeRef.current;
			const handleLoad = () => {
				// Check if iframe content is accessible (basic check)
				try {
					// If we can't access iframe content, it might be blocked
					// This is a best-effort check
					if (iframe.contentWindow === null) {
						setIframeError(true);
					}
				} catch {
					// Cross-origin restrictions are expected, but iframe should still work
					// Only set error if we get a specific blocking error
				}
			};

			const handleError = () => {
				setIframeError(true);
			};

			iframe.addEventListener('load', handleLoad);
			iframe.addEventListener('error', handleError);

			// Timeout fallback - if iframe doesn't load within 5 seconds, show fallback
			const timeout = setTimeout(() => {
				// Check if iframe has loaded content
				if (iframe.contentDocument === null && iframe.contentWindow === null) {
					// This might indicate blocking, but cross-origin restrictions are normal
					// We'll let the user try the iframe first
				}
			}, 5000);

			return () => {
				iframe.removeEventListener('load', handleLoad);
				iframe.removeEventListener('error', handleError);
				clearTimeout(timeout);
			};
		}
	}, [signatureUrl]);

	const handleOpenInNewTab = () => {
		if (!signatureUrl) {
			toast({
				message: 'Signature URL is not available. Please contact support.',
				variant: 'error',
			});
			return;
		}

		window.open(signatureUrl, '_blank', 'noopener,noreferrer');
	};

	if (!signatureUrl) {
		return (
			<Stack itemsSpacing='24'>
				<Stack itemsSpacing='8'>
					<Text size='body' weight='semibold'>
						{actionItem.config.title}
					</Text>
					<Text size='bodySmall' color='secondary'>
						Signature URL is not available. The document may not be ready yet.
					</Text>
				</Stack>
			</Stack>
		);
	}

	// If iframe is blocked or user prefers new tab, show button
	if (iframeError) {
		return (
			<Stack itemsSpacing='24'>
				<Stack itemsSpacing='8'>
					<Text size='body' weight='semibold'>
						{actionItem.config.title}
					</Text>
					<Text size='bodySmall' color='secondary'>
						{actionItem.config.instructions ||
							'Click the button below to complete the signing process in Pandadoc.'}
					</Text>
					<Text size='footnote' color='secondary'>
						The document will open in a new tab for security.
					</Text>
				</Stack>
				<Button
					size='large'
					isFullWidth
					onPress={handleOpenInNewTab}
					aria-label='Open Pandadoc signature page'>
					{actionItem.config.buttonTitle || 'Sign Document'}
				</Button>
			</Stack>
		);
	}

	// Try iframe embedding
	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				height: 'calc(100vh - 150px)', // Account for drawer header, padding, and title
				gap: '16px',
			}}>
			<Stack itemsSpacing='8'>
				<Text size='body' weight='semibold'>
					{actionItem.config.title}
				</Text>
				<Text size='bodySmall' color='secondary'>
					{actionItem.config.instructions ||
						'Complete the signing process in the document below.'}
				</Text>
			</Stack>

			<iframe
				ref={iframeRef}
				src={signatureUrl}
				style={{
					width: '100%',
					flex: 1,
					border: '1px solid var(--color-border)',
					borderRadius: '8px',
					backgroundColor: 'var(--color-background)',
					minHeight: 0,
				}}
				title='Pandadoc Signature Page'
				allow='clipboard-read; clipboard-write'
				sandbox='allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox'
			/>
		</div>
	);
};
